import { FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

type Lang = 'curl' | 'js' | 'python' | 'cpp';

const LANG_LABELS: Record<Lang, string> = {
  curl: 'curl',
  js: 'JavaScript',
  python: 'Python',
  cpp: 'C++ (ESP32)',
};

const CodeBlock: FC<{ children: string; lang?: string }> = ({ children, lang }) => (
  <pre
    className='bg-light-dark rounded p-4 mb-4'
    style={{
      color: '#e6edf3',
      backgroundColor: '#0d1117',
      fontSize: '13px',
      lineHeight: '1.5',
      overflowX: 'auto',
      whiteSpace: 'pre',
    }}
  >
    <code data-lang={lang}>{children}</code>
  </pre>
);

const Section: FC<{ id: string; title: string; children: React.ReactNode }> = ({
  id,
  title,
  children,
}) => (
  <section id={id} className='mb-10'>
    <h2 className='fw-bolder fs-1 text-gray-900 mb-4'>{title}</h2>
    {children}
  </section>
);

const TutorialWebSocketPage: FC = () => {
  const apiUrl = process.env.REACT_APP_API_URL ?? 'https://api.cmblindada.com.br';
  const wsUrl = process.env.REACT_APP_WS_URL ?? 'wss://ws.cmblindada.com.br';

  const [quickstartLang, setQuickstartLang] = useState<Lang>('curl');

  const examples = useMemo(
    () => ({
      curl: {
        provision: `# 1. Provisionar um token de dispositivo
curl -X POST "${apiUrl}/devices/<BLUETOOTH_ID>/provision" \\
  -H "Authorization: Bearer <SEU_ADMIN_JWT>" \\
  -H "Content-Type: application/json" \\
  -d '{"notes":"primeiro provisionamento"}'

# Resposta:
# { "token": "eyJhbGciOiJIUzI1NiIs...", "jti": "uuid-v4" }`,
        connect: `# 2. Testar conexão com websocat (ou wscat -c)
websocat "${wsUrl}/?token=<TOKEN_DO_DISPOSITIVO>"

# 3. Enviar um heartbeat após receber o "welcome"
{"type":"heartbeat","payload":{"firmware_version":"1.0.0","uptime_s":120,"rssi":-65}}`,
      },
      js: {
        provision: `// 1. Provisionar um token de dispositivo
const res = await fetch(\`${apiUrl}/devices/\${bluetoothId}/provision\`, {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${adminJwt}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({ notes: 'primeiro provisionamento' }),
});
const { token } = await res.json();`,
        connect: `// 2. Conectar ao gateway
const ws = new WebSocket(\`${wsUrl}/?token=\${token}\`);

ws.addEventListener('open', () => {
  console.log('conectado');
});

ws.addEventListener('message', (ev) => {
  const msg = JSON.parse(ev.data);
  console.log('recebido:', msg);

  if (msg.type === 'welcome') {
    // 3. Enviar heartbeat após autenticar
    ws.send(JSON.stringify({
      type: 'heartbeat',
      payload: { firmware_version: '1.0.0', uptime_s: 120, rssi: -65 },
    }));
  }
});

ws.addEventListener('close', (ev) => {
  console.log('fechado:', ev.code, ev.reason);
});`,
      },
      python: {
        provision: `# pip install requests websockets
import requests

# 1. Provisionar um token de dispositivo
res = requests.post(
    f"${apiUrl}/devices/{bluetooth_id}/provision",
    headers={"Authorization": f"Bearer {admin_jwt}"},
    json={"notes": "primeiro provisionamento"},
)
res.raise_for_status()
token = res.json()["token"]`,
        connect: `import asyncio
import json
import websockets

async def device_loop(token: str):
    uri = f"${wsUrl}/?token={token}"
    async with websockets.connect(uri) as ws:
        async for raw in ws:
            msg = json.loads(raw)
            print("recebido:", msg)

            if msg["type"] == "welcome":
                # 3. Enviar heartbeat
                await ws.send(json.dumps({
                    "type": "heartbeat",
                    "payload": {
                        "firmware_version": "1.0.0",
                        "uptime_s": 120,
                        "rssi": -65,
                    },
                }))

asyncio.run(device_loop(token))`,
      },
      cpp: {
        provision: `// Pré-requisito: o token de dispositivo é gerado uma vez pelo back-office
// (provisionamento via API) e gravado em NVS/flash do ESP32.
// O firmware NÃO chama o endpoint de provisão diretamente.

#include <Preferences.h>

Preferences prefs;
prefs.begin("cmb", true);
String token = prefs.getString("ws_token", "");
prefs.end();`,
        connect: `// Lib: WebSockets (Markus Sattler) - https://github.com/Links2004/arduinoWebSockets
#include <WiFi.h>
#include <WebSocketsClient.h>
#include <ArduinoJson.h>

WebSocketsClient ws;

void onEvent(WStype_t type, uint8_t *payload, size_t length) {
  switch (type) {
    case WStype_CONNECTED:
      Serial.println("[WS] conectado");
      break;
    case WStype_TEXT: {
      StaticJsonDocument<512> doc;
      deserializeJson(doc, payload, length);
      const char *msgType = doc["type"];
      Serial.printf("[WS] msg: %s\\n", msgType);

      if (strcmp(msgType, "welcome") == 0) {
        // Enviar heartbeat
        StaticJsonDocument<256> hb;
        hb["type"] = "heartbeat";
        JsonObject p = hb.createNestedObject("payload");
        p["firmware_version"] = "1.0.0";
        p["uptime_s"] = millis() / 1000;
        p["rssi"] = WiFi.RSSI();
        String out;
        serializeJson(hb, out);
        ws.sendTXT(out);
      }
      break;
    }
    case WStype_DISCONNECTED:
      Serial.println("[WS] desconectado");
      break;
    default: break;
  }
}

void setup() {
  // ... WiFi.begin(...)
  String url = "/?token=" + String(deviceToken);
  ws.beginSSL("ws.cmblindada.com.br", 443, url.c_str());
  ws.onEvent(onEvent);
  ws.setReconnectInterval(5000);
}

void loop() {
  ws.loop();
}`,
      },
    }),
    [apiUrl, wsUrl],
  );

  return (
    <div className='container-fluid'>
      <div className='row'>
        {/* TOC */}
        <aside className='col-lg-3 d-none d-lg-block'>
          <div
            className='position-sticky bg-white rounded shadow-sm p-5 mb-5'
            style={{ top: '90px' }}
          >
            <h6 className='fw-bolder text-gray-800 mb-3'>Nesta página</h6>
            <ul className='list-unstyled mb-0'>
              <li className='mb-2'><a href='#overview' className='text-gray-700 text-hover-primary'>Visão geral</a></li>
              <li className='mb-2'><a href='#endpoints' className='text-gray-700 text-hover-primary'>Endpoints</a></li>
              <li className='mb-2'><a href='#auth' className='text-gray-700 text-hover-primary'>Autenticação</a></li>
              <li className='mb-2'><a href='#quickstart' className='text-gray-700 text-hover-primary'>Quickstart</a></li>
              <li className='mb-2'><a href='#messages' className='text-gray-700 text-hover-primary'>Mensagens</a></li>
              <li className='mb-2'><a href='#close-codes' className='text-gray-700 text-hover-primary'>Códigos de fechamento</a></li>
              <li className='mb-2'><a href='#heartbeat' className='text-gray-700 text-hover-primary'>Heartbeat / ping</a></li>
              <li className='mb-2'><a href='#playground' className='text-gray-700 text-hover-primary'>Playground</a></li>
              <li className='mb-2'><a href='#troubleshooting' className='text-gray-700 text-hover-primary'>Troubleshooting</a></li>
            </ul>
          </div>
        </aside>

        {/* Content */}
        <div className='col-lg-9'>
          <div className='bg-white rounded shadow-sm p-8 mb-5'>
            <h1 className='fw-bolder fs-2hx text-gray-900 mb-3'>Tutorial WebSocket</h1>
            <p className='text-gray-600 fs-5 mb-0'>
              Como integrar dispositivos e aplicações ao <strong>CMB WebSocket Gateway</strong>{' '}
              para envio de heartbeats, alarmes em tempo real e recepção de comandos.
            </p>
          </div>

          <Section id='overview' title='Visão geral'>
            <p className='text-gray-700 fs-5'>
              O Gateway é um servidor WebSocket que mantém uma conexão persistente entre as
              caixas de medição (ou aplicações cliente) e a plataforma CMB. Ele é responsável por:
            </p>
            <ul className='text-gray-700 fs-5'>
              <li>Receber <strong>heartbeats</strong> periódicos dos dispositivos para indicar que estão online.</li>
              <li>Receber e processar <strong>eventos de alarme</strong> em tempo real (porta, vibração, energia etc.).</li>
              <li>Entregar <strong>comandos</strong> da plataforma para dispositivos conectados.</li>
              <li>Confirmar a execução dos comandos via <strong>command_ack</strong>.</li>
              <li>Fazer <em>push</em> de alterações para o painel web em tempo real.</li>
            </ul>
            <p className='text-gray-700 fs-5'>
              Toda comunicação usa frames JSON com o envelope <code>{`{ "type": "<tipo>", "payload": { ... } }`}</code>.
            </p>
          </Section>

          <Section id='endpoints' title='Endpoints'>
            <div className='table-responsive'>
              <table className='table table-bordered align-middle'>
                <thead className='fw-bold bg-light'>
                  <tr>
                    <th>Recurso</th>
                    <th>URL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>API REST (provisão / revogação de token)</td>
                    <td><code>{apiUrl}</code></td>
                  </tr>
                  <tr>
                    <td>Gateway WebSocket (dispositivo)</td>
                    <td><code>{wsUrl}/</code></td>
                  </tr>
                  <tr>
                    <td>Gateway WebSocket (cliente web)</td>
                    <td><code>{wsUrl}/web</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className='text-muted fs-6'>
              Os endereços acima refletem a configuração atual do ambiente em que você está
              autenticado (<code>REACT_APP_API_URL</code> / <code>REACT_APP_WS_URL</code>).
            </p>
          </Section>

          <Section id='auth' title='Autenticação'>
            <h4 className='fw-bolder text-gray-800 mt-5 mb-3'>Token de dispositivo</h4>
            <p className='text-gray-700 fs-5'>
              Cada caixa tem um JWT longo, emitido <strong>uma vez</strong> pelo endpoint
              de provisionamento e gravado no firmware. O token é assinado em HS256 e contém:
            </p>
            <CodeBlock lang='json'>{`{
  "sub": "<bluetooth_id>",   // identificador físico da caixa
  "box_id": 123,             // ID interno do measuring_box
  "jti": "<uuid>",           // identificador único para revogação
  "iat": 1700000000          // emitido em (opcional)
}`}</CodeBlock>

            <h4 className='fw-bolder text-gray-800 mt-5 mb-3'>Como enviar o token</h4>
            <p className='text-gray-700 fs-5'>O gateway aceita o JWT de duas formas:</p>
            <ul className='text-gray-700 fs-5'>
              <li><strong>Cabeçalho HTTP</strong> (preferido, para clientes que controlam o handshake): <code>Authorization: Bearer &lt;jwt&gt;</code></li>
              <li><strong>Query string</strong> (fallback para WebSocket nativo do browser): <code>?token=&lt;jwt&gt;</code></li>
            </ul>

            <h4 className='fw-bolder text-gray-800 mt-5 mb-3'>Revogação</h4>
            <p className='text-gray-700 fs-5'>
              Tokens podem ser revogados a qualquer momento pelo endpoint{' '}
              <code>POST /devices/&lt;bluetooth_id&gt;/revoke/&lt;jti&gt;</code>.
              Após revogado, a próxima reconexão será rejeitada com o código{' '}
              <strong>4403</strong>.
            </p>
          </Section>

          <Section id='quickstart' title='Quickstart'>
            <p className='text-gray-700 fs-5 mb-5'>
              Quatro passos pra ter um dispositivo conectado e enviando dados:
            </p>

            <ol className='text-gray-700 fs-5 mb-5'>
              <li className='mb-2'><strong>Provisione</strong> um token via API (precisa de um JWT de admin).</li>
              <li className='mb-2'><strong>Grave o token</strong> no firmware ou guarde em ambiente seguro.</li>
              <li className='mb-2'><strong>Conecte</strong> no gateway WebSocket usando o token.</li>
              <li className='mb-2'>Aguarde o frame <strong>welcome</strong> e comece a enviar mensagens.</li>
            </ol>

            <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6'>
              {(Object.keys(LANG_LABELS) as Lang[]).map((l) => (
                <li className='nav-item' key={l}>
                  <button
                    type='button'
                    className={`nav-link ${quickstartLang === l ? 'active' : ''}`}
                    onClick={() => setQuickstartLang(l)}
                  >
                    {LANG_LABELS[l]}
                  </button>
                </li>
              ))}
            </ul>

            <h5 className='fw-bolder text-gray-800 mb-3'>1 e 2 — Provisão do token</h5>
            <CodeBlock lang={quickstartLang}>{examples[quickstartLang].provision}</CodeBlock>

            <h5 className='fw-bolder text-gray-800 mb-3'>3 e 4 — Conexão e primeira mensagem</h5>
            <CodeBlock lang={quickstartLang}>{examples[quickstartLang].connect}</CodeBlock>
          </Section>

          <Section id='messages' title='Referência de mensagens'>
            <p className='text-gray-700 fs-5'>
              Todas as mensagens seguem o envelope{' '}
              <code>{`{ "type": "<tipo>", "payload": { ... } }`}</code>.
            </p>

            {/* WELCOME */}
            <div className='card mb-5'>
              <div className='card-header py-4 d-flex justify-content-between align-items-center'>
                <div>
                  <h4 className='fw-bolder mb-1'>welcome</h4>
                  <span className='text-muted fs-7'>servidor → dispositivo</span>
                </div>
                <span className='badge badge-light-success'>Recebido</span>
              </div>
              <div className='card-body'>
                <p className='text-gray-700'>
                  Enviado pelo gateway logo após uma conexão autenticada com sucesso.
                </p>
                <CodeBlock lang='json'>{`{
  "type": "welcome",
  "payload": {
    "bluetooth_id": "AA:BB:CC:DD:EE:FF",
    "server_time": "2025-02-15T10:30:45.123Z"
  }
}`}</CodeBlock>
              </div>
            </div>

            {/* HEARTBEAT */}
            <div className='card mb-5'>
              <div className='card-header py-4 d-flex justify-content-between align-items-center'>
                <div>
                  <h4 className='fw-bolder mb-1'>heartbeat</h4>
                  <span className='text-muted fs-7'>dispositivo → servidor</span>
                </div>
                <span className='badge badge-light-primary'>Enviado</span>
              </div>
              <div className='card-body'>
                <p className='text-gray-700'>
                  Sinaliza que a caixa está online. Recomenda-se enviar a cada 30s. O servidor
                  atualiza <code>last_seen_at</code> e propaga métricas opcionais.
                </p>
                <CodeBlock lang='json'>{`{
  "type": "heartbeat",
  "payload": {
    "firmware_version": "2.1.4",  // opcional
    "uptime_s": 3600,              // opcional
    "rssi": -65                    // opcional
  }
}`}</CodeBlock>
              </div>
            </div>

            {/* ALARM EVENT */}
            <div className='card mb-5'>
              <div className='card-header py-4 d-flex justify-content-between align-items-center'>
                <div>
                  <h4 className='fw-bolder mb-1'>alarm_event</h4>
                  <span className='text-muted fs-7'>dispositivo → servidor</span>
                </div>
                <span className='badge badge-light-primary'>Enviado</span>
              </div>
              <div className='card-body'>
                <p className='text-gray-700'>
                  Notifica mudança de estado de um sensor de alarme. O servidor grava o evento
                  e faz broadcast para os clientes web conectados.
                </p>
                <CodeBlock lang='json'>{`{
  "type": "alarm_event",
  "payload": {
    "alarm_type": "door",                // ver lista abaixo
    "active": true,                       // true = disparou, false = limpou
    "ts": "2025-02-15T10:32:10.500Z"     // opcional (default: agora)
  }
}`}</CodeBlock>
                <p className='text-gray-700 mb-2'><strong>Valores aceitos em <code>alarm_type</code>:</strong></p>
                <p className='text-gray-700 fs-6'>
                  <code>door</code>, <code>bolt</code>, <code>door_lock</code>, <code>vibration</code>,{' '}
                  <code>energy</code>, <code>sound</code>, <code>violation</code>,{' '}
                  <code>request_opening_of_safe</code>, <code>maximum_time_for_open_door</code>,{' '}
                  <code>cable_cut_alarm</code>
                </p>
              </div>
            </div>

            {/* COMMAND */}
            <div className='card mb-5'>
              <div className='card-header py-4 d-flex justify-content-between align-items-center'>
                <div>
                  <h4 className='fw-bolder mb-1'>command</h4>
                  <span className='text-muted fs-7'>servidor → dispositivo</span>
                </div>
                <span className='badge badge-light-success'>Recebido</span>
              </div>
              <div className='card-body'>
                <p className='text-gray-700'>
                  Solicita que o dispositivo execute uma ação. Deve ser respondido com{' '}
                  <code>command_ack</code> assim que a execução for finalizada (sucesso ou falha).
                </p>
                <CodeBlock lang='json'>{`{
  "type": "command",
  "payload": {
    "command_id": "42",
    "command_type_id": 5,
    "params": { "mode": "standby" },  // opcional
    "hash": "abc123def"                // opcional
  }
}`}</CodeBlock>
              </div>
            </div>

            {/* COMMAND ACK */}
            <div className='card mb-5'>
              <div className='card-header py-4 d-flex justify-content-between align-items-center'>
                <div>
                  <h4 className='fw-bolder mb-1'>command_ack</h4>
                  <span className='text-muted fs-7'>dispositivo → servidor</span>
                </div>
                <span className='badge badge-light-primary'>Enviado</span>
              </div>
              <div className='card-body'>
                <p className='text-gray-700'>
                  Confirma a execução de um <code>command</code>. Quando <code>executed=true</code>,
                  o servidor marca o comando como concluído no banco.
                </p>
                <CodeBlock lang='json'>{`{
  "type": "command_ack",
  "payload": {
    "command_id": "42",
    "executed": true,
    "details": { "duration_ms": 250 }   // opcional
  }
}`}</CodeBlock>
              </div>
            </div>
          </Section>

          <Section id='close-codes' title='Códigos de fechamento'>
            <p className='text-gray-700 fs-5'>
              O gateway usa códigos customizados na faixa 4xxx para sinalizar motivos de
              encerramento — útil pra implementar lógica de reconexão no firmware.
            </p>
            <div className='table-responsive'>
              <table className='table table-bordered align-middle'>
                <thead className='fw-bold bg-light'>
                  <tr>
                    <th style={{ width: '120px' }}>Código</th>
                    <th>Significado</th>
                    <th>O que fazer</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>1000</code></td>
                    <td>Fechamento normal</td>
                    <td>Reconectar normalmente</td>
                  </tr>
                  <tr>
                    <td><code>4401</code></td>
                    <td>Não autenticado (token inválido ou ausente)</td>
                    <td>Reprovisionar o token</td>
                  </tr>
                  <tr>
                    <td><code>4403</code></td>
                    <td>Token revogado</td>
                    <td>Não reconectar; pedir novo token ao back-office</td>
                  </tr>
                  <tr>
                    <td><code>4429</code></td>
                    <td>Conexão duplicada (mesmo dispositivo reconectou)</td>
                    <td>Não tentar; provavelmente outra instância está conectada</td>
                  </tr>
                  <tr>
                    <td><code>4500</code></td>
                    <td>Erro interno do servidor</td>
                    <td>Reconectar com backoff exponencial</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section id='heartbeat' title='Heartbeat / ping'>
            <p className='text-gray-700 fs-5'>
              Duas camadas independentes mantêm a conexão saudável:
            </p>
            <ul className='text-gray-700 fs-5'>
              <li>
                <strong>WebSocket ping/pong</strong> (gerenciado pela lib): o gateway envia{' '}
                <code>ping</code> a cada <strong>25s</strong> e desconecta se não receber{' '}
                <code>pong</code> em <strong>60s</strong>. A maioria das libs responde automaticamente
                — você não precisa fazer nada.
              </li>
              <li>
                <strong>Mensagem <code>heartbeat</code> da aplicação</strong>: enviada pelo
                dispositivo para atualizar <code>last_seen_at</code> no banco. Recomendado a cada
                30s; a janela esperada de silêncio é de 30s antes de ser considerado offline pelo
                monitoramento.
              </li>
            </ul>
          </Section>

          <Section id='playground' title='Playground'>
            <p className='text-gray-700 fs-5'>
              Para testes interativos sem precisar escrever código, o gateway hospeda um
              playground em <code>{wsUrl.replace(/^wss?:/, 'https:')}/playground</code>{' '}
              (em ambientes que tenham a feature habilitada).
            </p>
            <p className='text-gray-700 fs-5'>
              Lá você pode escolher entre <strong>Modo Dispositivo</strong> (cola um token
              provisionado e simula uma caixa) e <strong>Modo Cliente Web</strong> (usa o JWT
              da sua sessão pra ver o que o painel recebe em tempo real).
            </p>
          </Section>

          <Section id='troubleshooting' title='Troubleshooting'>
            <div className='accordion' id='troubleshooting-accordion'>
              <div className='accordion-item'>
                <h2 className='accordion-header'>
                  <button
                    className='accordion-button collapsed'
                    type='button'
                    data-bs-toggle='collapse'
                    data-bs-target='#tb-1'
                  >
                    Recebo 4401 ao conectar
                  </button>
                </h2>
                <div id='tb-1' className='accordion-collapse collapse' data-bs-parent='#troubleshooting-accordion'>
                  <div className='accordion-body'>
                    <p>Significa que o JWT não pôde ser validado. Verifique:</p>
                    <ul>
                      <li>O token não expirou (embora os tokens de dispositivo sejam longos)</li>
                      <li>O <code>jti</code> ainda existe na tabela <code>device_tokens</code></li>
                      <li>O <code>bluetooth_id</code> no <code>sub</code> corresponde a uma caixa válida</li>
                      <li>A URL inclui <code>?token=...</code> ou o cabeçalho <code>Authorization</code></li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className='accordion-item'>
                <h2 className='accordion-header'>
                  <button className='accordion-button collapsed' type='button' data-bs-toggle='collapse' data-bs-target='#tb-2'>
                    Recebo 4429 sempre que reconecto
                  </button>
                </h2>
                <div id='tb-2' className='accordion-collapse collapse' data-bs-parent='#troubleshooting-accordion'>
                  <div className='accordion-body'>
                    <p>
                      Indica que outra instância do mesmo dispositivo já está conectada. Causas
                      comuns: dois firmwares com o mesmo token rodando, ou reconexão antes do
                      timeout do servidor expirar a conexão anterior (60s). Aguarde ~70s ou
                      revise se há outra instância ativa.
                    </p>
                  </div>
                </div>
              </div>

              <div className='accordion-item'>
                <h2 className='accordion-header'>
                  <button className='accordion-button collapsed' type='button' data-bs-toggle='collapse' data-bs-target='#tb-3'>
                    Envio alarm_event e o painel não atualiza
                  </button>
                </h2>
                <div id='tb-3' className='accordion-collapse collapse' data-bs-parent='#troubleshooting-accordion'>
                  <div className='accordion-body'>
                    <p>Verifique:</p>
                    <ul>
                      <li>O <code>alarm_type</code> está entre os valores aceitos (veja a referência)</li>
                      <li>A caixa pertence à empresa do usuário logado no painel</li>
                      <li>O painel está com a tela de sensores ativa (a conexão WS é específica dela)</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className='accordion-item'>
                <h2 className='accordion-header'>
                  <button className='accordion-button collapsed' type='button' data-bs-toggle='collapse' data-bs-target='#tb-4'>
                    Mensagens minhas são rejeitadas
                  </button>
                </h2>
                <div id='tb-4' className='accordion-collapse collapse' data-bs-parent='#troubleshooting-accordion'>
                  <div className='accordion-body'>
                    <p>
                      O parser do gateway é estrito: a mensagem precisa ser um JSON válido com{' '}
                      <code>type</code> (string) e <code>payload</code> (objeto). Frames binários
                      não são suportados; mande tudo como texto.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          <div className='mt-10 pt-5 border-top'>
            <Link to='/dashboard' className='btn btn-sm btn-primary'>
              Voltar para a Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export { TutorialWebSocketPage };
